/**
 * interactionPreferencesAtoms.ts
 *
 * Human-Centered AI: Interaction Preferences (behavioral, not visual)
 *
 * These preferences shape *how the AI behaves toward the user*, as opposed to
 * the sensory/CSS adaptations in focusPreferencesAtoms. They are read by chat
 * and agent surfaces to decide whether to suggest vs. auto-apply, pace output,
 * and show reasoning.
 *
 * Design rationale:
 * - Autonomy (SDT; Shneiderman HCAI control+automation): default to presenting
 *   AI actions as suggestions the human confirms, keeping the human in control.
 * - Competence scaffolding (SDT; Cognitive Load Theory): optionally deliver long
 *   plans one step at a time instead of an overwhelming wall.
 * - Explainability (Shneiderman HCAI; Hong & Park five-stage): optionally always
 *   surface the AI's reasoning and uncertainty so trust is earned, not demanded.
 *
 * Defaults preserve current product behavior; each option is opt-in and
 * persisted for cross-session continuity.
 */

import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { getStorageForKey } from '@/lib/storage/storage-config'

/**
 * How AI-initiated actions are presented.
 * - 'suggest': present actions as suggestions the user confirms (autonomy-first)
 * - 'auto': allow the AI to apply low-risk actions directly (current behavior)
 */
export type SuggestionMode = 'suggest' | 'auto'

/**
 * How multi-step AI output is paced.
 * - 'standard': deliver the full plan/response at once (default)
 * - 'stepByStep': reveal one step at a time to reduce cognitive load
 */
export type Pacing = 'standard' | 'stepByStep'

/**
 * How much reasoning the AI surfaces by default.
 * - 'standard': show reasoning on request (default)
 * - 'always': always surface reasoning and uncertainty
 */
export type Explainability = 'standard' | 'always'

export interface InteractionPreferences {
  suggestionMode: SuggestionMode
  pacing: Pacing
  explainability: Explainability
}

export const defaultInteractionPreferences: InteractionPreferences = {
  suggestionMode: 'auto',
  pacing: 'standard',
  explainability: 'standard'
}

/**
 * Persisted, opt-in interaction preferences. Non-critical for first paint, so
 * it uses lazy storage via the shared router.
 */
export const interactionPreferencesAtom = atomWithStorage<InteractionPreferences>(
  'interactionPreferences',
  defaultInteractionPreferences,
  getStorageForKey('interactionPreferences')
)

// ===== Convenience derived atoms (read/write) =====

export const suggestionModeAtom = atom(
  (get) => get(interactionPreferencesAtom).suggestionMode,
  (get, set, next: SuggestionMode) => {
    set(interactionPreferencesAtom, { ...get(interactionPreferencesAtom), suggestionMode: next })
  }
)

export const pacingAtom = atom(
  (get) => get(interactionPreferencesAtom).pacing,
  (get, set, next: Pacing) => {
    set(interactionPreferencesAtom, { ...get(interactionPreferencesAtom), pacing: next })
  }
)

export const explainabilityAtom = atom(
  (get) => get(interactionPreferencesAtom).explainability,
  (get, set, next: Explainability) => {
    set(interactionPreferencesAtom, { ...get(interactionPreferencesAtom), explainability: next })
  }
)

/** True when the user prefers suggestions over auto-applied AI actions. */
export const prefersSuggestionsAtom = atom(
  (get) => get(interactionPreferencesAtom).suggestionMode === 'suggest'
)

/** True when the user prefers step-by-step pacing. */
export const prefersStepByStepAtom = atom(
  (get) => get(interactionPreferencesAtom).pacing === 'stepByStep'
)

/** True when the user wants reasoning surfaced by default. */
export const prefersAlwaysExplainAtom = atom(
  (get) => get(interactionPreferencesAtom).explainability === 'always'
)

/**
 * True when the user has opted into any non-default interaction adaptation.
 */
export const hasActiveInteractionAdaptationsAtom = atom((get) => {
  const prefs = get(interactionPreferencesAtom)
  return (
    prefs.suggestionMode !== defaultInteractionPreferences.suggestionMode ||
    prefs.pacing !== defaultInteractionPreferences.pacing ||
    prefs.explainability !== defaultInteractionPreferences.explainability
  )
})
